import { animated, SpringConfig, useSpring } from '@react-spring/web';
import React, { useEffect, useState } from 'react';
import debounce from 'lodash/debounce';
import { Nullable } from 'src/types/nullable.type';
import { useEditorStore } from '../editor-store';
import { ScrollDirection, useScrollDirection } from '../utils/scroll-direction.hook';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';
import { ContentEditableInput } from './content-editable-input';
import { isCmdZ } from '../utils/keyboard';
import { generateBlockId } from '../helpers/generate-block-id';

type Props = {
  journeyName: Nullable<string>;
  onJourneyNameChange: (name: string) => void;
};

const NAME_ANIMATION_CONFIG: SpringConfig = {
  mass: 1,
  tension: 400,
  friction: 40,
};

export const JourneyName: React.FC<Props> = ({ journeyName, onJourneyNameChange }) => {
  const [localJourneyName, setLocalJourneyName] = useState(journeyName);
  const { setCurrentSectionId, editorContentElement, canvasScrollPosition } = useEditorStore((state) => state);

  useEffect(() => {
    setLocalJourneyName(journeyName);
  }, [journeyName]);

  const [lastScrollDirection, setLastScrollDirection] = React.useState<ScrollDirection>('up');
  useScrollDirection(editorContentElement, setLastScrollDirection, 10);

  const showName = lastScrollDirection === 'up' || canvasScrollPosition.y === 0;

  const nameTransitionStyle = useSpring({
    opacity: showName ? 1 : 0,
    config: NAME_ANIMATION_CONFIG,
  });

  const onChange = useRefCallback(
    (name: string) => {
      if (name !== journeyName) {
        onJourneyNameChange(name);
      }
    },
    [onJourneyNameChange]
  );

  const debounceOnChange = React.useMemo(() => debounce(onChange, 500), [onChange]);

  const handlePaste = (event: React.ClipboardEvent) => {
    event.preventDefault();
    const paste = event?.clipboardData?.getData('text') || '';
    const selection = window.getSelection();
    selection?.deleteFromDocument();
    selection?.getRangeAt(0).insertNode(document.createTextNode(paste));
    const updatedJourneyName = (event.target as HTMLDivElement).innerText || '';
    onChange(updatedJourneyName);
  };

  return (
    <div className='absolute left-2/4 -translate-x-2/4 h-6 flex items-center bg-transparent'>
      <animated.div
        style={nameTransitionStyle}
        onClick={(value) => {
          const id = generateBlockId();
          setCurrentSectionId(id);
        }}
      >
        <ContentEditableInput
          className='w-auto px-8 neue-set-journey-name text-neue-journey-medium-strong'
          onChange={(value) => {
            setLocalJourneyName(value);
            debounceOnChange(value);
          }}
          onPaste={(event: React.ClipboardEvent) => handlePaste(event)}
          placeholder='Untitled'
          value={localJourneyName || ''}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.currentTarget.blur();
            } else if (isCmdZ(e.nativeEvent)) {
              debounceOnChange.cancel();
              const v = (e.target as HTMLDivElement).innerText || '';
              onChange(v);
            }
          }}
          onKeyPress={(e) => {
            if ((e.target as HTMLDivElement).innerText.length > 80) {
              e.preventDefault();
            }
          }}
        />
      </animated.div>
    </div>
  );
};
